import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { ethers } from "ethers";
import { createCoinbaseWalletSDK } from "@coinbase/wallet-sdk";
import axios from "axios";
import { useRouter } from "next/router";
import authFetch from "../utils/authFetch";
import { getClientGasOverrides } from "../utils/clientGasOverrides";
import {
  getMetaMaskProvider,
  hasNonMetaMaskProvider,
  waitForMetaMaskProvider,
} from "../utils/metamaskProvider";
import { pinFileToIPFS, pinJSONToIPFS } from "../services/ipfsService";

// Contract constants
import {
  NFT_MARKETPLACE_ADDRESS,
  NFT_MARKETPLACE_ABI,
  PINATA_CONFIG,
  DEFAULT_NETWORK,
  TARGET_NETWORK,
  SUPPORTED_NETWORKS,
  getRpcUrlByChainId,
  getAlchemyConfigByChainId,
  getReadContracts,
  resolveContractConfig,
} from "../constants/nftMarketplace";

// --- On-chain NFT cache helpers ---
const NFT_CACHE_PREFIX = "impactoverse_onchain_nfts_";
const NFT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCachedNFTs = (chainId) => {
  try {
    const raw = sessionStorage.getItem(`${NFT_CACHE_PREFIX}${chainId}`);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > NFT_CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
};

const setCachedNFTs = (chainId, data) => {
  try {
    sessionStorage.setItem(
      `${NFT_CACHE_PREFIX}${chainId}`,
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // sessionStorage full or unavailable
  }
};

// Drop every chain's cached listing snapshot. Called after any transaction
// that changes what's listed (buy/list/resell) so this browser immediately
// refetches; other visitors may see up-to-5-minute-stale LISTS (acceptable),
// but detail pages read live state and stay honest.
const clearCachedNFTs = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(NFT_CACHE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // sessionStorage unavailable
  }
};

// Normalize the metadata `images` array (all pinned files, minted for
// multi-image tokens) — legacy single-image tokens fall back to [image].
const toImageList = (images, image) => {
  const list = Array.isArray(images)
    ? images.filter((url) => typeof url === "string" && url)
    : [];
  if (list.length > 0) return list;
  return image ? [image] : [];
};

// Preload images into browser cache
const preloadImages = (items) => {
  if (typeof window === "undefined") return;
  items.forEach((item) => {
    if (item.image) {
      const img = new Image();
      img.src = item.image;
    }
  });
};

// Conservative gas-limit fallback for the affordability pre-flight when
// estimateGas fails (it can revert for reasons unrelated to funds — including
// the shortfall itself). Sized to what a createMarketSale actually uses
// (~394k) so the allowance stays honest without blocking on a bad estimate.
const BUY_GAS_LIMIT_FALLBACK = 400000n;

// Format a wei amount as a short POL figure for user-facing messages: enough
// decimals to distinguish a small gas allowance, without 18-decimal noise.
const formatPol = (wei) => {
  const value = Number(ethers.formatEther(wei));
  if (!Number.isFinite(value)) return ethers.formatEther(wei);
  if (value === 0) return "0";
  return value
    .toFixed(value >= 1 ? 3 : 6)
    .replace(/0+$/, "")
    .replace(/\.$/, "");
};

// An error whose message is already written for the buyer (e.g. the pre-flight
// shortfall message). humanizeBuyError passes these through untouched instead
// of collapsing them into its generic fallback.
const buyerFacingError = (message) => {
  const err = new Error(message);
  err.isBuyerFacing = true;
  return err;
};

// Map a createMarketSale failure to a plain-language message. Ethers surfaces
// on-chain reverts and precondition failures as CALL_EXCEPTION / estimateGas
// errors whose raw text ("missing revert data", "CALL_EXCEPTION") means nothing
// to a buyer. We inspect the nested error fields for the cases a buyer can
// actually act on and fall back to a generic-but-honest message otherwise.
const humanizeBuyError = (error) => {
  if (error?.isBuyerFacing) {
    return error.message;
  }
  if (error?.code === "ACTION_REJECTED" || error?.code === 4001) {
    return "Transaction rejected in your wallet.";
  }
  const haystack = [
    error?.reason,
    error?.shortMessage,
    error?.info?.error?.message,
    error?.error?.message,
    error?.data?.message,
    error?.message,
  ]
    .filter(Boolean)
    .join(" | ")
    .toLowerCase();

  if (error?.code === "INSUFFICIENT_FUNDS" || haystack.includes("insufficient funds")) {
    return "You don't have enough POL to cover this price plus gas.";
  }
  if (haystack.includes("fiat-only")) {
    // A fiat-only listing sells by card, not crypto. The FE now filters these
    // out of the crypto views, so this only fires on a stale link.
    return "This piece is sold by card, not crypto. Open it from the card-sale listing to buy it.";
  }
  if (haystack.includes("reserved for a pending card purchase")) {
    // A dual-rail listing is reserved while someone's card checkout is open.
    // The UI disables Buy on known lock state; this catches the race where
    // the reservation landed after the page loaded.
    return "Reserved — a card purchase of this piece is in progress. If that purchase doesn't complete, the piece becomes buyable again within an hour.";
  }
  if (haystack.includes("cannot buy") || haystack.includes("own listing") || haystack.includes("seller")) {
    return "You can't buy your own listing.";
  }
  if (haystack.includes("not listed") || haystack.includes("already sold")) {
    return "This listing is no longer available — it may have just sold.";
  }
  return (
    error?.reason ||
    error?.shortMessage ||
    "Couldn't complete the purchase. The listing may be unavailable, or the price may exceed your balance."
  );
};

// Supported wallet providers - Only MetaMask and Coinbase Smart Wallet
const WALLET_PROVIDERS = {
  METAMASK: "metamask",
  COINBASE_SMART_WALLET: "coinbase_smart_wallet",
};

// Initialize Coinbase Wallet SDK
let coinbaseWallet = null;
let coinbaseProvider = null;

if (typeof window !== "undefined") {
  coinbaseWallet = createCoinbaseWalletSDK({
    appName: "ImpactoVerse Impact Art Marketplace",
    appLogoUrl: "/logo.png",
    darkMode: false,
  });

  // Use the provider from the SDK
  coinbaseProvider = coinbaseWallet.getProvider();
}

// Create the NFT Marketplace Context
const NFTMarketplaceContext = createContext();

// Custom hook to use the NFT Marketplace Context
export const useNFTMarketplace = () => {
  const context = useContext(NFTMarketplaceContext);
  if (!context) {
    throw new Error(
      "useNFTMarketplace must be used within an NFTMarketplaceProvider",
    );
  }
  return context;
};

// Helper function to create provider with Alchemy RPC URL
const createProvider = (chainId = 137) => {
  const rpcUrl = getRpcUrlByChainId(chainId);
  return new ethers.JsonRpcProvider(rpcUrl);
};

// Helper function to get contract instance (V1 — used as the safe default for
// legacy/untagged items and for reads that predate contract-aware routing).
const fetchContract = (signerOrProvider) =>
  new ethers.Contract(
    NFT_MARKETPLACE_ADDRESS,
    NFT_MARKETPLACE_ABI,
    signerOrProvider,
  );

// Build the contract instance for a specific address+abi pair.
const contractAt = (signerOrProvider, address, abi) =>
  new ethers.Contract(address, abi, signerOrProvider);

// Resolve the ethers.Contract for a token's OWN marketplace contract. Every
// on-chain action (buy/resell/live-state) routes through this so a V1 token is
// only ever hit with a V1 call and a V2 token with a V2 call. When the item
// carries no contractAddress (legacy link / pre-V2 cache) it resolves to V1 —
// correct, because such items are provably V1 (see resolveContractConfig).
const contractForItem = (signerOrProvider, item) => {
  const { address, abi } = resolveContractConfig(item?.contractAddress);
  return contractAt(signerOrProvider, address, abi);
};

// NFT Marketplace Provider Component
export const NFTMarketplaceProvider = ({ children }) => {
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  // False until the on-mount auto-reconnect attempt has finished (or was
  // skipped). Consumers that nudge "connect your wallet" must wait for this,
  // or they fire falsely during the reconnect window.
  const [walletCheckSettled, setWalletCheckSettled] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  const [provider, setProvider] = useState(null);
  const [networkId, setNetworkId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [walletProvider, setWalletProvider] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Helper function to connect with smart contract
  const connectingWithSmartContract = async () => {
    try {
      if (!provider) {
        throw new Error("Provider not available");
      }

      const signer = await provider.getSigner();
      const contract = fetchContract(signer);
      return contract;
    } catch (error) {
      console.error("Error connecting with smart contract:", error);
      throw error;
    }
  };

  // Upload file to IPFS via the backend pinning proxy (Pinata credentials
  // stay server-side)
  const uploadToIPFS = async (file) => {
    try {
      const res = await pinFileToIPFS(file, {
        pinataMetadata: { name: file.name },
        pinataOptions: { cidVersion: 0 },
      });

      const url = `${PINATA_CONFIG.GATEWAY_URL}${res.IpfsHash}`;
      return url;
    } catch (error) {
      console.error("Error uploading to Pinata IPFS:", error);
      throw error;
    }
  };

  // Create NFT function
  const createNFT = async (
    name,
    price,
    image,
    description,
    router,
    campaignWalletAddress = null,
    causeWalletAddress = null,
    impactPercentage = 2500,
  ) => {
    if (!name || !description || !price || !image) {
      throw new Error("All fields are required");
    }

    try {
      // Upload metadata to IPFS via the backend pinning proxy
      const res = await pinJSONToIPFS({ name, description, image });

      const url = `${PINATA_CONFIG.GATEWAY_URL}${res.IpfsHash}`;

      // Create sale on blockchain with impact recipients
      await createSale(
        url,
        price,
        false,
        null,
        campaignWalletAddress,
        causeWalletAddress,
        impactPercentage,
      );

      if (router) {
        router.push("/dashboard/dots");
      }

      return { success: true, tokenURI: url };
    } catch (error) {
      console.error("Error while creating NFT:", error);
      throw error;
    }
  };

  // Create sale function.
  //
  // The resell branch (resellToken) routes to the token's OWN contract via
  // `contractAddress` so a V1 token is relisted on V1 and a V2 token on V2 —
  // never cross-called. Untagged → V1 (legacy tokens).
  //
  // The create branch (createToken) is legacy/inert: no UI path mints through
  // it (create-dot mints via NFTMarketplaceUtils). New mints must go through
  // the primary-listing resolver, so this branch is left V1-only and unused.
  const createSale = async (
    url,
    formInputPrice,
    isReselling = false,
    id = null,
    campaignWalletAddress = null,
    causeWalletAddress = null,
    impactPercentage = 2500, // Default 25% for impact funding
    contractAddress = null,
  ) => {
    try {
      const price = ethers.parseUnits(formInputPrice.toString(), "ether");
      if (!provider) {
        throw new Error("Provider not available");
      }
      const signer = await provider.getSigner();
      const gas = await getClientGasOverrides(provider, networkId);

      const transaction = !isReselling
        ? await fetchContract(signer).createToken(
            url,
            price,
            campaignWalletAddress || ethers.ZeroAddress,
            causeWalletAddress || ethers.ZeroAddress,
            impactPercentage,
            gas,
          )
        : await contractForItem(signer, { contractAddress }).resellToken(
            id,
            price,
            gas,
          );

      await transaction.wait();
      // New/relisted item — cached listing snapshots are stale now.
      clearCachedNFTs();
      return transaction;
    } catch (error) {
      console.error("Error while creating sale:", error);
      throw error;
    }
  };

  // Read + hydrate the listed market items for ONE marketplace contract.
  // Every returned item is tagged with `contractAddress` (the contract it came
  // from) so downstream buy/resell/live-state route to the token's real
  // contract. `contractAddress` here is the loop's address — NOT a hardwired
  // constant — so V1 and V2 items are tagged correctly.
  const readMarketItemsForContract = async (
    { address, abi },
    chainId,
    web3Provider,
  ) => {
    const contract = contractAt(web3Provider, address, abi);

    let data;
    try {
      data = await contract.fetchMarketItems();
    } catch (error) {
      if (error.code === "BAD_DATA" && error.value === "0x") {
        return [];
      }
      console.error(
        `Error calling fetchMarketItems on ${address}:`,
        error?.message || error,
      );
      // Isolate per-contract failures: a V1 outage must not blank V2 and vice
      // versa. Return nothing for this contract; the union of the rest stands.
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // The crypto marketplace read must NEVER surface a fiat-only listing. On V2
    // a card-sale piece is created with fiatOnly=true and escrowed exactly like
    // a crypto listing, so it comes back from fetchMarketItems too — but its
    // only buy path is the off-chain card rail (BE card-listings registry), and
    // createMarketSale reverts "Fiat-only listing" on it. Drop those here so a
    // card piece never appears with an ETH price / crypto Buy. V1's MarketItem
    // has no fiatOnly field (undefined → kept), so V1 items are unaffected.
    data = data.filter((item) => item.fiatOnly !== true);
    if (data.length === 0) {
      return [];
    }

    let items = null;
    const alchemyConfig = getAlchemyConfigByChainId(chainId);

    if (alchemyConfig?.apiKey) {
      try {
        const { Alchemy, Network } = await import("alchemy-sdk");
        const networkMap = {
          137: Network.MATIC_MAINNET,
          80002: Network.MATIC_AMOY,
        };
        const alchemyNetwork = networkMap[chainId];

        if (alchemyNetwork) {
          const alchemy = new Alchemy({
            apiKey: alchemyConfig.apiKey,
            network: alchemyNetwork,
          });

          const tokenBatch = data.map((item) => ({
            contractAddress: address,
            tokenId: Number(item.tokenId).toString(),
          }));

          const batchResponse =
            await alchemy.nft.getNftMetadataBatch(tokenBatch);

          // Response is { nfts: [...] }, not a plain array
          const batchNfts = batchResponse?.nfts || batchResponse;
          const nftList = Array.isArray(batchNfts) ? batchNfts : [];

          // Check if Alchemy actually resolved metadata (not just errors)
          const hasValidMetadata = nftList.some(
            (meta) =>
              meta?.image?.cachedUrl ||
              meta?.image?.originalUrl ||
              meta?.raw?.metadata?.image,
          );

          if (!hasValidMetadata || nftList.length === 0) {
            throw new Error("Alchemy returned no usable metadata");
          }

          items = data.map(
            (
              {
                tokenId,
                seller,
                owner,
                price: unformattedPrice,
                campaignOwner,
                causeOwner,
                impactPercentage,
                locked,
              },
              index,
            ) => {
              const meta = nftList[index];
              const rawMeta = meta?.raw?.metadata || {};

              // Alchemy cachedUrl is CDN-served (fast), originalUrl may be IPFS
              const image =
                meta?.image?.cachedUrl ||
                meta?.image?.thumbnailUrl ||
                meta?.image?.originalUrl ||
                rawMeta.image ||
                "";

              return {
                price: ethers.formatUnits(
                  unformattedPrice.toString(),
                  "ether",
                ),
                tokenId: Number(tokenId),
                seller,
                owner,
                image,
                images: toImageList(rawMeta.images, image),
                impactData: rawMeta.impactData || null,
                name: meta?.name || rawMeta.name || "",
                description: meta?.description || rawMeta.description || "",
                tokenURI: meta?.tokenUri || "",
                campaignOwner,
                causeOwner,
                impactPercentage: Number(impactPercentage),
                contractAddress: address,
                // V2 reservation flag (card checkout in flight). V1 items
                // carry no such field → false.
                locked: locked === true,
              };
            },
          );
        }
      } catch (alchemyError) {
        console.warn(
          "Alchemy batch API failed, falling back to tokenURI:",
          alchemyError.message,
        );
        items = null; // Fall through to legacy approach
      }
    }

    // Fallback: original tokenURI + IPFS approach (parallel)
    if (!items) {
      items = await Promise.all(
        data.map(
          async ({
            tokenId,
            seller,
            owner,
            price: unformattedPrice,
            campaignOwner,
            causeOwner,
            impactPercentage,
            locked,
          }) => {
            try {
              const tokenURI = await contract.tokenURI(tokenId);
              const {
                data: { image, name, description, images, impactData },
              } = await axios.get(tokenURI);
              const price = ethers.formatUnits(
                unformattedPrice.toString(),
                "ether",
              );
              return {
                price,
                tokenId: Number(tokenId),
                seller,
                owner,
                image,
                images: toImageList(images, image),
                impactData: impactData || null,
                name,
                description,
                tokenURI,
                campaignOwner,
                causeOwner,
                impactPercentage: Number(impactPercentage),
                contractAddress: address,
                locked: locked === true,
              };
            } catch (error) {
              console.error(`Error processing token ${tokenId}:`, error);
              return null;
            }
          },
        ),
      );
    }

    return items.filter((item) => item !== null);
  };

  // Fetch NFTs function — reads EVERY configured marketplace contract (V1 and
  // V2), tags each item with its source contract, and unions the results.
  // Uses Alchemy batch API + caching for speed.
  const fetchNFTs = async () => {
    try {
      const chainId = networkId || DEFAULT_NETWORK.chainId;

      // 1. Return cached data if fresh (instant load on tab switch / revisit)
      const cached = getCachedNFTs(chainId);
      if (cached) {
        preloadImages(cached);
        return cached;
      }

      // 2. Read each configured contract (V1 only if deployed on this chain;
      //    V2 when configured). A contract with an empty address is skipped by
      //    getReadContracts, so Amoy (no V1) simply reads V2 without crashing.
      const web3Provider = createProvider(chainId);
      const readContracts = getReadContracts();

      const perContract = await Promise.all(
        readContracts.map((c) =>
          readMarketItemsForContract(c, chainId, web3Provider),
        ),
      );

      const result = perContract.flat();

      // 3. Cache + preload images
      setCachedNFTs(chainId, result);
      preloadImages(result);

      return result;
    } catch (error) {
      console.error("Error while fetching NFTs:", error);
      throw error;
    }
  };

  // Read the caller's owned / listed items for ONE contract, tagging each with
  // its source `contractAddress`. The signer-bound contract scopes
  // fetchMyNFTs/fetchItemsListed to msg.sender.
  const readMyNFTsForContract = async ({ address, abi }, type, chainId, signer) => {
    const contract = contractAt(signer, address, abi);

    let data;
    try {
      data =
        type === "fetchItemsListed"
          ? await contract.fetchItemsListed()
          : await contract.fetchMyNFTs();
    } catch (error) {
      if (error.code === "BAD_DATA" && error.value === "0x") {
        return [];
      }
      // Isolate per-contract failures so one contract can't blank the other.
      console.error(
        `Error reading ${type} on ${address}:`,
        error?.message || error,
      );
      return [];
    }

    if (!data || data.length === 0) return [];

    // Same fiat-only exclusion as the public crypto read: a V2 fiatOnly item is
    // a card-sale piece whose only buy path is the off-chain card rail, so it
    // must not appear in the wallet's on-chain listed/owned crypto views (a
    // crypto Buy/resell on it reverts "Fiat-only listing"). V1 items carry no
    // fiatOnly field (undefined → kept). Portfolio COUNTS come from balanceOf
    // elsewhere, so this filter doesn't undercount owned tokens.
    data = data.filter((item) => item.fiatOnly !== true);
    if (data.length === 0) return [];

    const alchemyConfig = getAlchemyConfigByChainId(chainId);
    let items = null;

    if (alchemyConfig?.apiKey) {
      try {
        const { Alchemy, Network } = await import("alchemy-sdk");
        const networkMap = {
          137: Network.MATIC_MAINNET,
          80002: Network.MATIC_AMOY,
        };
        const alchemyNetwork = networkMap[chainId];

        if (alchemyNetwork) {
          const alchemy = new Alchemy({
            apiKey: alchemyConfig.apiKey,
            network: alchemyNetwork,
          });

          const tokenBatch = data.map((item) => ({
            contractAddress: address,
            tokenId: Number(item.tokenId).toString(),
          }));

          const batchResponse =
            await alchemy.nft.getNftMetadataBatch(tokenBatch);

          const batchNfts = batchResponse?.nfts || batchResponse;
          const nftList = Array.isArray(batchNfts) ? batchNfts : [];

          const hasValidMetadata = nftList.some(
            (meta) =>
              meta?.image?.cachedUrl ||
              meta?.image?.originalUrl ||
              meta?.raw?.metadata?.image,
          );

          if (!hasValidMetadata || nftList.length === 0) {
            throw new Error("Alchemy returned no usable metadata");
          }

          items = data.map(
            (
              {
                tokenId,
                seller,
                owner,
                price: unformattedPrice,
                campaignOwner,
                causeOwner,
                impactPercentage,
                locked,
              },
              index,
            ) => {
              const meta = nftList[index];
              const rawMeta = meta?.raw?.metadata || {};
              const image =
                meta?.image?.cachedUrl ||
                meta?.image?.thumbnailUrl ||
                meta?.image?.originalUrl ||
                rawMeta.image ||
                "";
              return {
                price: ethers.formatUnits(
                  unformattedPrice.toString(),
                  "ether",
                ),
                tokenId: Number(tokenId),
                seller,
                owner,
                image,
                images: toImageList(rawMeta.images, image),
                impactData: rawMeta.impactData || null,
                name: meta?.name || rawMeta.name || "",
                description: meta?.description || rawMeta.description || "",
                tokenURI: meta?.tokenUri || "",
                campaignOwner,
                causeOwner,
                impactPercentage: Number(impactPercentage),
                contractAddress: address,
                locked: locked === true,
              };
            },
          );
        }
      } catch (alchemyError) {
        console.warn(
          "Alchemy batch failed for my NFTs, falling back:",
          alchemyError.message,
        );
        items = null;
      }
    }

    // Fallback: original tokenURI + IPFS approach
    if (!items) {
      items = await Promise.all(
        data.map(
          async ({
            tokenId,
            seller,
            owner,
            price: unformattedPrice,
            campaignOwner,
            causeOwner,
            impactPercentage,
            locked,
          }) => {
            const price = ethers.formatUnits(
              unformattedPrice.toString(),
              "ether",
            );
            // A token the wallet demonstrably owns must always appear, even
            // if its metadata can't be resolved (e.g. IPFS unreachable on
            // localhost). Fall back to the on-chain tokenURI + a placeholder
            // image rather than dropping the item.
            let tokenURI = "";
            try {
              tokenURI = await contract.tokenURI(tokenId);
              const {
                data: { image, name, description, images, impactData },
              } = await axios.get(tokenURI);
              return {
                price,
                tokenId: Number(tokenId),
                seller,
                owner,
                image,
                images: toImageList(images, image),
                impactData: impactData || null,
                name,
                description,
                tokenURI,
                campaignOwner,
                causeOwner,
                impactPercentage: Number(impactPercentage),
                contractAddress: address,
                locked: locked === true,
              };
            } catch (error) {
              console.error(
                `Metadata unresolved for token ${tokenId}, showing placeholder:`,
                error.message,
              );
              return {
                price,
                tokenId: Number(tokenId),
                seller,
                owner,
                image: "",
                images: [],
                impactData: null,
                name: "",
                description: "",
                tokenURI,
                campaignOwner,
                causeOwner,
                impactPercentage: Number(impactPercentage),
                contractAddress: address,
                locked: locked === true,
              };
            }
          },
        ),
      );
    }

    return items.filter((item) => item !== null);
  };

  // Fetch my NFTs or listed NFTs — spans EVERY configured contract (V1 + V2)
  // and tags each item with its source contract. With Alchemy batch API.
  const fetchMyNFTsOrListedNFTs = async (type) => {
    try {
      if (!provider) {
        throw new Error("Provider not available");
      }
      const signer = await provider.getSigner();
      const chainId = networkId || DEFAULT_NETWORK.chainId;
      const readContracts = getReadContracts();

      const perContract = await Promise.all(
        readContracts.map((c) =>
          readMyNFTsForContract(c, type, chainId, signer),
        ),
      );

      const result = perContract.flat();
      preloadImages(result);
      return result;
    } catch (error) {
      console.error("Error while fetching listed NFTs:", error);
      throw error;
    }
  };

  // Buy NFT function. Routes createMarketSale to the token's OWN contract
  // (from nft.contractAddress) so a V1 token is bought on V1 and a V2 token on
  // V2 — never cross-called. An untagged nft resolves to V1 (legacy items).
  const buyNFT = async (nft) => {
    try {
      if (!provider) {
        throw new Error("Provider not available");
      }
      const signer = await provider.getSigner();
      const contract = contractForItem(signer, nft);
      const price = ethers.parseUnits(nft.price.toString(), "ether");
      const gas = await getClientGasOverrides(provider, networkId);

      // Pre-flight affordability check: a buyer who can't cover price + gas
      // should get one clear message BEFORE the wallet popup, not a raw
      // estimateGas/CALL_EXCEPTION error after it. The check may only block
      // on a genuine shortfall — any failure inside it (RPC hiccup, missing
      // address, no fee data) falls through to the normal buy attempt.
      let shortfall = null;
      try {
        const buyerAddress = await signer.getAddress();
        const balance = await provider.getBalance(buyerAddress);
        let gasLimit;
        try {
          gasLimit = await contract.createMarketSale.estimateGas(nft.tokenId, {
            value: price,
            ...gas,
          });
        } catch (_) {
          gasLimit = BUY_GAS_LIMIT_FALLBACK;
        }
        // Per-gas price: the 1559 ceiling we bid on Polygon-family chains, or
        // the node's own price on chains where getClientGasOverrides returns
        // {} (localhost) — zero if even that can't be read, so the check
        // degrades to price-only rather than blocking.
        let feePerGas = gas.maxFeePerGas ?? null;
        if (feePerGas == null) {
          try {
            const feeData = await provider.getFeeData();
            feePerGas = feeData?.gasPrice ?? feeData?.maxFeePerGas ?? 0n;
          } catch (_) {
            feePerGas = 0n;
          }
        }
        const gasAllowance = gasLimit * feePerGas;
        const totalNeeded = price + gasAllowance;
        if (balance < totalNeeded) {
          const breakdown =
            gasAllowance > 0n
              ? ` (${formatPol(price)} price + ~${formatPol(gasAllowance)} gas)`
              : "";
          shortfall =
            `You need about ${formatPol(totalNeeded)} POL for this purchase` +
            `${breakdown}, but your wallet has ${formatPol(balance)} POL. ` +
            "Please add funds to your wallet and try again.";
        }
      } catch (preflightError) {
        // The check itself must never break buying — proceed to the wallet.
        console.warn("Buy pre-flight check skipped:", preflightError?.message);
      }
      if (shortfall) {
        throw buyerFacingError(shortfall);
      }

      // createMarketSale is payable and requires msg.value == the listing
      // price, so the buyer's payment IS this value. A too-low/absent value
      // reverts on-chain; sending the exact listed price is the whole payment.
      // Gas overrides are merged in AFTER value so a fee field can never
      // overwrite the payment.
      const transaction = await contract.createMarketSale(nft.tokenId, {
        value: price,
        ...gas,
      });

      await transaction.wait();
      // The listing set just changed — invalidate cached snapshots so the
      // sold piece disappears from this browser's marketplace immediately.
      clearCachedNFTs();
      return transaction;
    } catch (error) {
      console.error("Error while buying NFT:", error);
      // A raw CALL_EXCEPTION / estimateGas failure is opaque to buyers. Map the
      // known revert/precondition cases to a plain-language message so the UI
      // never shows a cryptic gas error for an ordinary, explainable situation.
      throw new Error(humanizeBuyError(error));
    }
  };

  // Live listing state for one token. Detail pages must stay honest even
  // when the cached market list is stale (e.g. right after a purchase):
  // a token held by the marketplace contract is escrowed/listed; any other
  // holder means it was sold (or never listed).
  const getLiveListingState = async (tokenId, contractAddress) => {
    const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
    // Read ownership + escrow against the token's OWN contract: a V1 token
    // escrowed in V1 must not read as un-escrowed just because it isn't in V2,
    // and vice versa. Untagged → V1 (legacy tokens).
    const { address, abi } = resolveContractConfig(contractAddress);
    const contract = contractAt(web3Provider, address, abi);
    // ownerOf + tokenURI in one round trip: the detail page's fallback path
    // (a piece not in any of the viewer's lists — moved/sold/contributed) has
    // no cached metadata, so it rebuilds the view straight from tokenURI here.
    // ownerOf reverts for a non-existent token, distinguishing "token gone"
    // from an RPC outage upstream.
    const [owner, tokenURI] = await Promise.all([
      contract.ownerOf(tokenId),
      contract.tokenURI(tokenId),
    ]);
    return {
      owner,
      escrowed: owner.toLowerCase() === address.toLowerCase(),
      tokenURI,
    };
  };

  // Signs a direct ERC-721 transfer of a token the connected wallet holds to
  // `destinationAddress` (used to donate an owned on-chain Impact Art into
  // platform custody for a campaign). The marketplace contract is itself the
  // ERC-721, so the same instance carries safeTransferFrom. Routes to the
  // token's OWN contract via `contractAddress` so a V2-owned piece transfers on
  // V2 and a V1-owned piece on V1 — never cross-called. Untagged → V1 (legacy
  // held tokens). Resolves after the transfer is mined and returns the tx hash;
  // rejects (nothing moved) if the wallet isn't connected, isn't on the right
  // network, or the user declines.
  const transferOwnedTokenTo = async (
    tokenId,
    destinationAddress,
    contractAddress = null,
  ) => {
    if (!provider || !walletAddress) {
      throw new Error("Connect your wallet to contribute this Impact Art.");
    }
    const networkCheck = validateNetworkForNFT();
    if (!networkCheck.isValid) {
      throw new Error(networkCheck.message);
    }
    const signer = await provider.getSigner();
    const contract = contractForItem(signer, { contractAddress });
    const gas = await getClientGasOverrides(provider, networkId);
    const tx = await contract["safeTransferFrom(address,address,uint256)"](
      walletAddress,
      destinationAddress,
      tokenId,
      gas,
    );
    await tx.wait();
    return { transactionHash: tx.hash };
  };

  // Get commission rate
  const getCommissionRate = async () => {
    try {
      const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
      const contract = fetchContract(web3Provider);
      const commissionRate = await contract.getCommissionRate();
      return Number(commissionRate) / 100; // Convert from basis points to percentage
    } catch (error) {
      console.error("Error getting commission rate:", error);
      throw error;
    }
  };

  // Calculate commission for a given price
  const calculateCommission = async (price) => {
    try {
      const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
      const contract = fetchContract(web3Provider);
      const priceInWei = ethers.parseUnits(price.toString(), "ether");
      const commissionInWei = await contract.calculateCommission(priceInWei);
      return ethers.formatUnits(commissionInWei.toString(), "ether");
    } catch (error) {
      console.error("Error calculating commission:", error);
      throw error;
    }
  };

  // Calculate seller amount after commission
  const calculateSellerAmount = async (price) => {
    try {
      const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
      const contract = fetchContract(web3Provider);
      const priceInWei = ethers.parseUnits(price.toString(), "ether");
      const sellerAmountInWei =
        await contract.calculateSellerAmount(priceInWei);
      return ethers.formatUnits(sellerAmountInWei.toString(), "ether");
    } catch (error) {
      console.error("Error calculating seller amount:", error);
      throw error;
    }
  };

  // Get price breakdown for display
  const getPriceBreakdown = async (price, impactPercentage = 2500) => {
    try {
      const commission = await calculateCommission(price);
      const commissionRate = await getCommissionRate();

      // Calculate impact funding
      const impactFunding = (parseFloat(price) * impactPercentage) / 10000;
      const campaignAmount = impactFunding / 2;
      const causeAmount = impactFunding / 2;
      const sellerAmountWithImpact =
        parseFloat(price) - parseFloat(commission) - impactFunding;

      return {
        totalPrice: price,
        commission: commission,
        sellerAmount: sellerAmountWithImpact.toString(),
        impactFunding: impactFunding.toString(),
        campaignAmount: campaignAmount.toString(),
        causeAmount: causeAmount.toString(),
        commissionRate: commissionRate,
        commissionPercentage: `${commissionRate}%`,
        impactPercentage: impactPercentage / 100,
      };
    } catch (error) {
      console.error("Error getting price breakdown:", error);
      throw error;
    }
  };

  // Get price breakdown for a specific market item
  const getPriceBreakdownForItem = async (tokenId) => {
    try {
      const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
      const contract = fetchContract(web3Provider);
      const breakdown = await contract.getPriceBreakdownForItem(tokenId);

      return {
        totalPrice: ethers.formatUnits(
          breakdown.totalPrice.toString(),
          "ether",
        ),
        commission: ethers.formatUnits(
          breakdown.commission.toString(),
          "ether",
        ),
        sellerAmount: ethers.formatUnits(
          breakdown.sellerAmount.toString(),
          "ether",
        ),
        impactFunding: ethers.formatUnits(
          breakdown.impactFunding.toString(),
          "ether",
        ),
        campaignAmount: ethers.formatUnits(
          breakdown.campaignAmount.toString(),
          "ether",
        ),
        causeAmount: ethers.formatUnits(
          breakdown.causeAmount.toString(),
          "ether",
        ),
      };
    } catch (error) {
      console.error("Error getting item price breakdown:", error);
      throw error;
    }
  };

  // Get total earnings accumulated by marketplace
  const getTotalEarnings = async () => {
    try {
      const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
      const contract = fetchContract(web3Provider);
      const totalEarnings = await contract.getTotalEarnings();
      return ethers.formatUnits(totalEarnings.toString(), "ether");
    } catch (error) {
      console.error("Error getting total earnings:", error);
      throw error;
    }
  };

  // Get current contract balance
  const getContractBalance = async () => {
    try {
      const web3Provider = createProvider(networkId || DEFAULT_NETWORK.chainId);
      const contract = fetchContract(web3Provider);
      const balance = await contract.getContractBalance();
      return ethers.formatUnits(balance.toString(), "ether");
    } catch (error) {
      console.error("Error getting contract balance:", error);
      throw error;
    }
  };

  // Withdraw specific amount (owner only)
  const withdrawEarnings = async (amount) => {
    try {
      const contract = await connectingWithSmartContract();
      const amountInWei = ethers.parseUnits(amount.toString(), "ether");
      const gas = await getClientGasOverrides(provider, networkId);

      const transaction = await contract.withdrawEarnings(amountInWei, gas);
      await transaction.wait();
      return transaction;
    } catch (error) {
      console.error("Error withdrawing earnings:", error);
      throw error;
    }
  };

  // Withdraw all earnings (owner only)
  const withdrawAllEarnings = async () => {
    try {
      const contract = await connectingWithSmartContract();
      const gas = await getClientGasOverrides(provider, networkId);

      const transaction = await contract.withdrawAllEarnings(gas);
      await transaction.wait();
      return transaction;
    } catch (error) {
      console.error("Error withdrawing all earnings:", error);
      throw error;
    }
  };

  // Get earnings dashboard data
  const getEarningsDashboard = async () => {
    try {
      const [totalEarnings, contractBalance, commissionRate] =
        await Promise.all([
          getTotalEarnings(),
          getContractBalance(),
          getCommissionRate(),
        ]);

      return {
        totalEarnings: totalEarnings,
        contractBalance: contractBalance,
        availableToWithdraw: contractBalance,
        commissionRate: commissionRate,
        commissionPercentage: `${commissionRate}%`,
      };
    } catch (error) {
      console.error("Error getting earnings dashboard:", error);
      throw error;
    }
  };

  // Get blockchain dashboard stats (POL) from the backend DB.
  //
  // These figures used to be computed in the browser by scanning the chain for
  // MarketItemSold events on every dashboard load — which repeatedly failed on
  // free public RPCs (dead endpoints, block-range caps, archive gating) and was
  // slow and rate-limit-exposed. The backend now records a complete POL row per
  // crypto sale (buyer/seller ids + the POL split), so the same DB-backed
  // dashboard-stats endpoint that already serves the fiat figures serves these.
  // No RPC call, no archive dependency.
  //
  // Returns the same shape the chain scan did (raw POL numeric strings + integer
  // counts) so the dashboard consumers format it unchanged. POL and USD figures
  // stay separate lines everywhere — they are never summed.
  const getBlockchainDashboardStats = async () => {
    const baseUrl =
      process.env.NEXT_PUBLIC_BASE_URL || process.env.NEXT_PUBLIC_BASE_URL_DEV;
    const response = await authFetch(`${baseUrl}/api/user/dashboard/stats`, {
      method: "GET",
    });
    if (!response?.success) {
      throw new Error(response?.message || "Failed to load on-chain stats");
    }
    const bc = response.data?.blockchain || {};
    return {
      purchases: bc.purchases || 0,
      contributions: bc.polContributions ?? "0",
      totalSales: bc.polSales ?? "0",
      impactRaised: bc.polImpactRaised ?? "0",
      portfolio: bc.portfolio || 0,
    };
  };

  // Detect available wallet providers
  const detectWalletProviders = useCallback(() => {
    const providers = [];

    if (typeof window !== "undefined") {
      // MetaMask - Check for MetaMask extension
      const metaMaskProvider = getMetaMaskProvider();
      if (metaMaskProvider) {
        providers.push({
          name: "MetaMask",
          type: WALLET_PROVIDERS.METAMASK,
          icon: "🦊",
          provider: metaMaskProvider,
          description: "Popular browser extension wallet",
          available: true,
        });
      }

      // Coinbase Smart Wallet - Always available through SDK
      providers.push({
        name: "Coinbase Smart Wallet",
        type: WALLET_PROVIDERS.COINBASE_SMART_WALLET,
        icon: "🔵",
        provider: coinbaseProvider,
        description: "Smart wallet that works without extensions",
        available: true,
      });
    }

    return providers;
  }, []);

  // Check if any wallet is connected
  const isWalletConnectedCheck = useCallback(async () => {
    try {
      if (typeof window === "undefined") {
        return false;
      }

      let accounts = [];
      let currentProvider = null;
      let currentWalletProvider = null;

      // Check for stored preference to prioritize the right wallet
      const storedProvider = localStorage.getItem("impactoverse_wallet_provider");

      // Helper function to check a provider
      const checkProvider = async (provider, providerType) => {
        try {
          const accts = await provider.request({ method: "eth_accounts" });
          if (accts && accts.length > 0) {
            return { accounts: accts, provider, providerType };
          }
        } catch (error) {
          if (error.code !== 4100 && error.code !== 4001) {
            console.error(`Error checking ${providerType}:`, error);
          }
        }
        return null;
      };

      // Check providers in order of preference
      if (storedProvider === WALLET_PROVIDERS.COINBASE_SMART_WALLET && coinbaseProvider) {
        // User previously connected with Coinbase, check it first
        const result = await checkProvider(coinbaseProvider, WALLET_PROVIDERS.COINBASE_SMART_WALLET);
        if (result) {
          accounts = result.accounts;
          currentProvider = result.provider;
          currentWalletProvider = result.providerType;
        }
      }

      // Check MetaMask if no connection yet. A stored MetaMask preference
      // means the extension is expected, so wait out its async injection —
      // a synchronous check here silently breaks auto-reconnect on refresh.
      const metaMaskProvider =
        storedProvider === WALLET_PROVIDERS.METAMASK
          ? await waitForMetaMaskProvider()
          : getMetaMaskProvider();
      if (accounts.length === 0 && metaMaskProvider) {
        const result = await checkProvider(metaMaskProvider, WALLET_PROVIDERS.METAMASK);
        if (result) {
          accounts = result.accounts;
          currentProvider = result.provider;
          currentWalletProvider = result.providerType;
        }
      }

      // Check Coinbase if no connection yet
      if (accounts.length === 0 && coinbaseProvider) {
        const result = await checkProvider(coinbaseProvider, WALLET_PROVIDERS.COINBASE_SMART_WALLET);
        if (result) {
          accounts = result.accounts;
          currentProvider = result.provider;
          currentWalletProvider = result.providerType;
        }
      }

      const isConnected = accounts && accounts.length > 0;
      setIsWalletConnected(isConnected);

      if (isConnected && currentProvider) {
        setWalletAddress(accounts[0]);
        setAccounts(accounts);
        setWalletProvider(currentWalletProvider);

        // Get network ID
        const chainId = await currentProvider.request({
          method: "eth_chainId",
        });
        setNetworkId(parseInt(chainId, 16));

        // Set up provider
        const ethersProvider = new ethers.BrowserProvider(currentProvider);
        setProvider(ethersProvider);
      }

      return isConnected;
    } catch (error) {
      console.error("Error checking wallet connection:", error);
      setError(error.message);
      return false;
    }
  }, []);

  // Read the persisted wallet binding for the signed-in user: the saved
  // address and whether its ownership has been proven by signature.
  const fetchWalletBinding = async () => {
    try {
      const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || process.env.NEXT_PUBLIC_BASE_URL_DEV;
      const response = await authFetch(`${baseUrl}/api/user/user-profile/wallet`, {
        method: "GET",
      });
      return {
        address: response?.walletAddress || null,
        verified: Boolean(response?.walletVerified),
      };
    } catch (error) {
      console.warn("[Wallet] Could not read binding:", error.message);
      return { address: null, verified: false };
    }
  };

  // Prove control of the connected wallet with a personal_sign, binding it to
  // the account for on-chain sale payouts. Fetches a fresh single-use nonce,
  // has the wallet sign the returned message, and posts the signature for
  // server-side recovery. No transaction is sent and no funds move. A wallet is
  // only persisted once this succeeds — connecting alone no longer saves it.
  // addressOverride covers the "just connected in this same action" case, where
  // the walletAddress state hasn't flushed into this closure yet.
  const verifyWalletBinding = async (addressOverride) => {
    const address = addressOverride || walletAddress;
    if (!address) {
      return { success: false, error: "Connect a wallet first." };
    }
    // Raw EIP-1193 provider for personal_sign (the ethers provider is not used
    // here so signing works the same for MetaMask and Coinbase).
    const raw =
      walletProvider === WALLET_PROVIDERS.COINBASE_SMART_WALLET
        ? coinbaseProvider
        : getMetaMaskProvider() || coinbaseProvider;
    if (!raw?.request) {
      return { success: false, error: "No wallet provider available to sign with." };
    }

    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || process.env.NEXT_PUBLIC_BASE_URL_DEV;
    try {
      const nonceRes = await authFetch(`${baseUrl}/api/user/user-profile/wallet/nonce`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address }),
      });
      if (!nonceRes?.message) {
        return { success: false, error: nonceRes?.message || "Couldn't start wallet verification." };
      }

      let signature;
      try {
        signature = await raw.request({
          method: "personal_sign",
          params: [nonceRes.message, address],
        });
      } catch (signError) {
        if (signError?.code === 4001) {
          return { success: false, rejected: true, error: "Signature request was cancelled." };
        }
        return { success: false, error: signError?.message || "Couldn't sign the verification message." };
      }

      const bindRes = await authFetch(`${baseUrl}/api/user/user-profile/wallet`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ walletAddress: address, signature }),
      });
      return { success: true, verified: Boolean(bindRes?.walletVerified) };
    } catch (error) {
      // authFetch throws on non-2xx with the server's message.
      console.warn("[Wallet] Verification failed:", error.message);
      return { success: false, error: error?.message || "Wallet verification failed." };
    }
  };

  // Connect to a specific wallet provider
  const connectWallet = async (providerType = WALLET_PROVIDERS.METAMASK) => {
    try {
      setIsLoading(true);
      setError(null);

      let targetProvider = null;
      let accounts = [];

      if (providerType === WALLET_PROVIDERS.METAMASK) {
        // Connect to MetaMask. Wait out its async injection before deciding
        // it's absent, and tell "another wallet owns window.ethereum" apart
        // from "not installed" — both used to read as "not installed".
        const metaMaskProvider = await waitForMetaMaskProvider();
        if (!metaMaskProvider) {
          throw new Error(
            hasNonMetaMaskProvider()
              ? "MetaMask isn't the active wallet. Set MetaMask as your default wallet (or disable the other wallet extension) and try again."
              : "MetaMask not detected. If you just installed it, refresh this page and try again — otherwise install the MetaMask extension.",
          );
        }

        targetProvider = metaMaskProvider;
        accounts = await targetProvider.request({
          method: "eth_requestAccounts",
        });
      } else if (providerType === WALLET_PROVIDERS.COINBASE_SMART_WALLET) {
        // Connect to Coinbase Smart Wallet
        if (!coinbaseProvider) {
          throw new Error("Coinbase Wallet SDK is not available.");
        }

        targetProvider = coinbaseProvider;
        accounts = await targetProvider.request({
          method: "eth_requestAccounts",
        });
      } else {
        throw new Error("Unsupported wallet provider type.");
      }

      if (accounts && accounts.length > 0) {
        setWalletAddress(accounts[0]);
        setAccounts(accounts);
        setIsWalletConnected(true);
        setWalletProvider(providerType);

        // Persist wallet provider preference for reconnection on refresh
        localStorage.setItem("impactoverse_wallet_provider", providerType);

        // Get network ID
        const chainId = await targetProvider.request({
          method: "eth_chainId",
        });
        setNetworkId(parseInt(chainId, 16));

        // Set up provider
        const ethersProvider = new ethers.BrowserProvider(targetProvider);
        setProvider(ethersProvider);

        // Connecting no longer silently persists the address: the wallet is
        // saved only after the user proves ownership via verifyWalletBinding
        // (Settings -> Crypto wallet), which the payout-relevant checks require.

        return {
          success: true,
          address: accounts[0],
          accounts: accounts,
          chainId: parseInt(chainId, 16),
          provider: providerType,
        };
      } else {
        throw new Error("No accounts found. Please check your wallet.");
      }
    } catch (err) {
      const errorMessage =
        err.code === 4001
          ? "Connection rejected by user"
          : err.message || "Failed to connect wallet";

      setError(errorMessage);
      console.error("Wallet connection error:", err);

      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Disconnect wallet function
  const disconnectWallet = () => {
    setIsWalletConnected(false);
    setWalletAddress(null);
    setProvider(null);
    setNetworkId(null);
    setError(null);
    setWalletProvider(null);
    setAccounts([]);
    // Clear stored wallet preference
    localStorage.removeItem("impactoverse_wallet_provider");
  };

  // Check if wallet is connected - main function requested by user
  const checkWalletConnection = useCallback(async () => {
    try {
      if (typeof window === "undefined") {
        return {
          isConnected: false,
          message: "No window object available",
        };
      }

      let accounts = [];
      let currentProvider = null;
      let providerType = null;

      // Check MetaMask first
      const metaMaskProvider = getMetaMaskProvider();
      if (metaMaskProvider) {
        try {
          accounts = await metaMaskProvider.request({
            method: "eth_accounts",
          });
          if (accounts.length > 0) {
            currentProvider = metaMaskProvider;
            providerType = WALLET_PROVIDERS.METAMASK;
          }
        } catch (error) {
          console.error("Error checking MetaMask:", error);
        }
      }

      // If no MetaMask connection, check Coinbase (but don't auto-request accounts)
      if (accounts.length === 0 && coinbaseProvider) {
        try {
          // Only check for already connected accounts, don't request permission
          accounts = await coinbaseProvider.request({
            method: "eth_accounts",
          });
          if (accounts.length > 0) {
            currentProvider = coinbaseProvider;
            providerType = WALLET_PROVIDERS.COINBASE_SMART_WALLET;
          }
        } catch (error) {
          // Silently handle the error - this is expected if no permission granted
          if (error.code !== 4100) {
            console.error("Error checking Coinbase:", error);
          }
        }
      }

      const isConnected = accounts && accounts.length > 0;

      if (isConnected && currentProvider) {
        const chainId = await currentProvider.request({
          method: "eth_chainId",
        });

        return {
          isConnected: true,
          address: accounts[0],
          accounts: accounts,
          chainId: parseInt(chainId, 16),
          provider: providerType,
        };
      } else {
        return {
          isConnected: false,
          message: "No wallet connected",
        };
      }
    } catch (error) {
      console.error("Error checking wallet connection:", error);
      return {
        isConnected: false,
        error: error.message,
        message: "Error checking wallet connection",
      };
    }
  }, []);

  // Get wallet connection status with detailed information
  const getWalletConnectionStatus = () => {
    return {
      isConnected: isWalletConnected,
      address: walletAddress,
      chainId: networkId,
      provider: provider,
      walletProvider: walletProvider,
      accounts: accounts,
      availableProviders: detectWalletProviders(),
      isLoading: isLoading,
      error: error,
    };
  };

  // Get balance of connected wallet
  const getWalletBalance = async () => {
    if (!provider || !walletAddress) return null;

    try {
      const balance = await provider.getBalance(walletAddress);
      return ethers.formatEther(balance);
    } catch (err) {
      console.error("Error getting wallet balance:", err);
      return null;
    }
  };

  // Switch network
  // A BrowserProvider bound to the wallet's CURRENT chain. The context
  // `provider` is created once at connect time and ethers v6 caches the
  // detected network on first RPC — after a mid-flow
  // wallet_switchEthereumChain, every call on that old instance rejects with
  // NETWORK_ERROR ("network changed: <old> => <new>") until reload. Callers
  // that just switched chains (or connected mid-submit, before state flushed)
  // must run their transaction on a fresh instance from the same raw injected
  // provider switchNetwork drives.
  const getFreshBrowserProvider = () => {
    const raw =
      walletProvider === WALLET_PROVIDERS.COINBASE_SMART_WALLET
        ? coinbaseProvider
        : getMetaMaskProvider();
    return raw ? new ethers.BrowserProvider(raw) : null;
  };

  const switchNetwork = async (targetChainId) => {
    const currentProvider =
      walletProvider === WALLET_PROVIDERS.COINBASE_SMART_WALLET
        ? coinbaseProvider
        : getMetaMaskProvider();

    if (!currentProvider) return false;

    try {
      await currentProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: `0x${targetChainId.toString(16)}` }],
      });

      // Update network ID in state
      setNetworkId(targetChainId);
      return true;
    } catch (err) {
      console.error("Error switching network:", err);

      // If the network doesn't exist in wallet, try to add it
      if (err.code === 4902) {
        try {
          await addNetwork(targetChainId);
          return true;
        } catch (addError) {
          console.error("Error adding network:", addError);
          return false;
        }
      }

      return false;
    }
  };

  // Add network to wallet
  const addNetwork = async (chainId) => {
    const currentProvider =
      walletProvider === WALLET_PROVIDERS.COINBASE_SMART_WALLET
        ? coinbaseProvider
        : getMetaMaskProvider();

    if (!currentProvider) {
      throw new Error("No wallet provider available");
    }

    // Wallet chain parameters come from the shared network table so every
    // supported network (including local Hardhat) can be added.
    const networkConfig = Object.values(SUPPORTED_NETWORKS).find(
      (network) => network.chainId === chainId,
    )?.walletParams;
    if (!networkConfig) {
      throw new Error(
        `Network configuration not found for chain ID: ${chainId}`,
      );
    }

    await currentProvider.request({
      method: "wallet_addEthereumChain",
      params: [networkConfig],
    });
  };

  // Validate current network for NFT operations — only the configured
  // target network is accepted.
  const validateNetworkForNFT = () => {
    if (!networkId) {
      return {
        isValid: false,
        message: "Network not detected. Please connect your wallet.",
        requiredAction: "connect",
      };
    }

    if (networkId !== TARGET_NETWORK.chainId) {
      return {
        isValid: false,
        message: `Wrong network. Please switch to ${TARGET_NETWORK.name} (Chain ID: ${TARGET_NETWORK.chainId}).`,
        requiredAction: "switch",
        suggestedChainId: TARGET_NETWORK.chainId,
      };
    }

    return {
      isValid: true,
      message: "Network is supported for NFT operations.",
      chainId: networkId,
    };
  };

  // Get network name
  const getNetworkName = (chainId = networkId) => {
    const networkNames = {
      137: "Polygon Mainnet",
      80002: "Polygon Amoy Testnet",
      31337: "Localhost",
    };

    return networkNames[chainId] || `Unknown Network (${chainId})`;
  };

  // Effect to monitor wallet connection state on mount
  // Always check for existing wallet connections on page load/refresh
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Check for stored wallet provider preference
    const storedWalletProvider = localStorage.getItem("impactoverse_wallet_provider");

    // Only auto-reconnect if the user previously connected via the app AND has not
    // manually disconnected. disconnectWallet() removes this stored preference, so a
    // manual disconnect now persists across refreshes. (MetaMask keeps the site
    // authorized after an in-app disconnect, so eth_accounts alone would otherwise
    // keep reporting the account and silently reconnect on every page load.)
    const hasWalletExtension = getMetaMaskProvider() || coinbaseProvider;

    if (storedWalletProvider && hasWalletExtension) {
      Promise.resolve(isWalletConnectedCheck()).finally(() =>
        setWalletCheckSettled(true),
      );
    } else {
      // Nothing to reconnect — the "not connected" state is already final.
      setWalletCheckSettled(true);
    }
  }, [isWalletConnectedCheck]);

  // Effect to handle account changes
  useEffect(() => {
    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else {
        setWalletAddress(accounts[0]);
        setAccounts(accounts);
      }
    };

    const handleChainChanged = (chainId) => {
      setNetworkId(parseInt(chainId, 16));
    };

    // Set up event listeners for both providers.
    // MetaMask is resolved asynchronously (it may inject after mount, or live
    // in window.ethereum.providers[] behind another wallet) and the resolved
    // object is kept so cleanup removes listeners from the SAME provider they
    // were added to.
    let unmounted = false;
    let boundMetaMaskProvider = null;

    if (typeof window !== "undefined") {
      waitForMetaMaskProvider().then((metaMaskProvider) => {
        if (unmounted || !metaMaskProvider) return;
        boundMetaMaskProvider = metaMaskProvider;
        metaMaskProvider.on("accountsChanged", handleAccountsChanged);
        metaMaskProvider.on("chainChanged", handleChainChanged);
      });

      // Coinbase event listeners
      if (coinbaseProvider) {
        coinbaseProvider.on("accountsChanged", handleAccountsChanged);
        coinbaseProvider.on("chainChanged", handleChainChanged);
      }
    }

    return () => {
      // Clean up event listeners
      unmounted = true;
      if (boundMetaMaskProvider) {
        boundMetaMaskProvider.removeListener(
          "accountsChanged",
          handleAccountsChanged,
        );
        boundMetaMaskProvider.removeListener(
          "chainChanged",
          handleChainChanged,
        );
      }

      if (typeof window !== "undefined" && coinbaseProvider) {
        coinbaseProvider.removeListener(
          "accountsChanged",
          handleAccountsChanged,
        );
        coinbaseProvider.removeListener("chainChanged", handleChainChanged);
      }
    };
  }, []);

  const value = {
    // State
    isWalletConnected,
    walletCheckSettled,
    walletAddress,
    provider,
    networkId,
    isLoading,
    error,
    walletProvider,
    accounts,

    // Functions
    checkWalletConnection,
    connectWallet,
    disconnectWallet,
    fetchWalletBinding,
    verifyWalletBinding,
    getWalletConnectionStatus,
    getWalletBalance,
    switchNetwork,
    addNetwork,
    getFreshBrowserProvider,
    validateNetworkForNFT,
    getNetworkName,
    detectWalletProviders,
    uploadToIPFS,
    createNFT,
    createSale,
    fetchNFTs,
    fetchMyNFTsOrListedNFTs,
    buyNFT,
    getLiveListingState,
    transferOwnedTokenTo,
    getCommissionRate,
    calculateCommission,
    calculateSellerAmount,
    getPriceBreakdown,
    getPriceBreakdownForItem,
    getTotalEarnings,
    getContractBalance,
    withdrawEarnings,
    withdrawAllEarnings,
    getEarningsDashboard,
    getBlockchainDashboardStats,

    // Constants
    WALLET_PROVIDERS,
  };

  return (
    <NFTMarketplaceContext.Provider value={value}>
      {children}
    </NFTMarketplaceContext.Provider>
  );
};
